import React, { useEffect, useRef } from "react";

import Spinner from "../../shared/ui/Loading/Spinner";

/**
 * Feature-local confirmation for blocking a user
 * (ACTION-MENUS-UX-B1-04 / B1-11; NOTIFICATION-COMPLETION-LIVE-QA-CORRECTIONS-A1-R1).
 * Pattern mirrors UserListModal's project-modal shell + ClientDeleteConfirmModal
 * pending/ESC rules. Not a global confirm framework.
 *
 * `context`:
 * - "profile" (default): UserScreen copy — historical profile-block wording.
 * - "private_chat": truthful private-chat contract — history remains visible;
 *   new messaging is disabled; profile hiding is not claimed.
 */
function UserBlockConfirmModal({
  open,
  userName,
  pending,
  onCancel,
  onConfirm,
  context = "profile",
}) {
  const previousOverflowRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !pending) {
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    if (previousOverflowRef.current === null) {
      previousOverflowRef.current = document.body.style.overflow;
    }
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      if (previousOverflowRef.current !== null) {
        document.body.style.overflow = previousOverflowRef.current;
        previousOverflowRef.current = null;
      }
    };
  }, [open, pending, onCancel]);

  if (!open) return null;

  const safeName = (userName || "").trim() || "цього користувача";
  const isPrivateChat = context === "private_chat";

  const handleOverlayMouseDown = (e) => {
    if (pending) return;
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <>
      <div
        className="modal fade show project-modal"
        id="user-block-confirm"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-block-confirm-title"
        style={{ display: "block" }}
        onMouseDown={handleOverlayMouseDown}
        data-pending={pending ? "true" : "false"}
        data-block-context={isPrivateChat ? "private_chat" : "profile"}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title" id="user-block-confirm-title">
                Заблокувати користувача
              </h3>
              <button
                type="button"
                className="modal-btn-close"
                aria-label="Закрити вікно підтвердження"
                onClick={onCancel}
                disabled={pending}
              >
                <i className="fa-solid fa-xmark fa-2x" aria-hidden="true" />
              </button>
            </div>
            <div className="modal-body">
              {isPrivateChat ? (
                <p data-testid="user-block-confirm-body">
                  Заблокувати <strong>{safeName}</strong>? Після блокування ви
                  не зможете обмінюватися новими повідомленнями в цьому
                  приватному чаті. Історія чату залишиться доступною.
                </p>
              ) : (
                <p data-testid="user-block-confirm-body">
                  Заблокувати <strong>{safeName}</strong>? Ви більше не
                  бачитимете його профіль і повідомлення від нього.
                </p>
              )}
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button
                  type="button"
                  className="btn user-block-confirm__cancel"
                  onClick={onCancel}
                  disabled={pending}
                >
                  Скасувати
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={onConfirm}
                  disabled={pending}
                  data-loading={pending ? "true" : undefined}
                >
                  {pending ? (
                    <Spinner size="sm" label="Блокування" />
                  ) : (
                    <>
                      <i
                        className="fa-solid fa-ban me-1"
                        aria-hidden="true"
                      />
                      Заблокувати
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}

export default UserBlockConfirmModal;
